/**
 * KanbanAvatar — tiny, deterministic avatar component used by:
 *   - the card face (a horizontal avatar stack of every assignee)
 *   - the drawer's member picker (single avatar with name beside it)
 *
 * Rendering rules:
 *   - When the member ships an `avatar_url` we render the image.
 *   - Otherwise we fall back to initials on a background color that
 *     hashes from the member's id (so the same person is always the
 *     same color across the app — easier to scan a board).
 */

import type { KanbanMember } from '../api'

export interface KanbanAvatarProps {
  member: KanbanMember
  /** Pixel size of the avatar (square). Defaults to 24. */
  size?: number
  /** When true, render a thin white ring (used inside avatar stacks
   *  to visually separate overlapping avatars). */
  ring?: boolean
  /** Optional CSS class hook. */
  className?: string
}

const PALETTE = [
  ['#fee2e2', '#991b1b'], // red
  ['#ffedd5', '#9a3412'], // orange
  ['#fef3c7', '#92400e'], // amber
  ['#d1fae5', '#065f46'], // emerald
  ['#dbeafe', '#1e40af'], // blue
  ['#ede9fe', '#5b21b6'], // violet
  ['#fce7f3', '#9d174d'], // pink
  ['#e0f2fe', '#075985'], // sky
] as const

function colorFor(id: number): readonly [string, string] {
  // Deterministic per-id hash so the same user always gets the same
  // avatar tint. Modulo the palette length keeps it cheap.
  const slot = ((id % PALETTE.length) + PALETTE.length) % PALETTE.length
  return PALETTE[slot]
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function KanbanAvatar({
  member,
  size = 24,
  ring = false,
  className = '',
}: KanbanAvatarProps) {
  const [bg, fg] = colorFor(member.id)
  const dimension = `${size}px`
  const fontSize = `${Math.round(size * 0.42)}px`

  if (member.avatar_url) {
    return (
      <img
        src={member.avatar_url}
        alt={member.name}
        title={member.name}
        className={[
          'inline-block rounded-full object-cover',
          ring ? 'ring-2 ring-white dark:ring-neutral-950' : '',
          className,
        ].join(' ')}
        style={{ width: dimension, height: dimension }}
      />
    )
  }

  return (
    <span
      title={member.name}
      role="img"
      aria-label={member.name}
      className={[
        'inline-flex items-center justify-center rounded-full font-semibold select-none',
        ring ? 'ring-2 ring-white dark:ring-neutral-950' : '',
        className,
      ].join(' ')}
      style={{
        width: dimension,
        height: dimension,
        background: bg,
        color: fg,
        fontSize,
        lineHeight: 1,
      }}
    >
      {initialsOf(member.name)}
    </span>
  )
}

export interface KanbanAvatarStackProps {
  members: KanbanMember[]
  /** Maximum visible avatars before collapsing into a "+N" pill. */
  max?: number
  /** Per-avatar pixel size. */
  size?: number
}

/**
 * Horizontal stack of avatars used on the card face. Truncates to
 * `max` and renders a "+N" pill for overflow.
 */
export function KanbanAvatarStack({
  members,
  max = 3,
  size = 22,
}: KanbanAvatarStackProps) {
  if (members.length === 0) return null
  const visible = members.slice(0, max)
  const overflow = members.length - visible.length
  return (
    <div className="flex -space-x-1.5">
      {visible.map((m) => (
        <KanbanAvatar key={m.id} member={m} size={size} ring />
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex items-center justify-center rounded-full bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200 ring-2 ring-white dark:ring-neutral-950 font-semibold"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            fontSize: `${Math.round(size * 0.42)}px`,
          }}
          title={`${overflow} more`}
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}
